// FinanceDashboard.js - 100% ORIGINAL UI + ALL TASKS DONE

import React, { useEffect, useState, useContext, createContext } from "react";
import { Line, Bar } from "react-chartjs-2";
import { get } from "../../services/api";
import FullScreenLoader from "../../components/FullScreenLoader";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Context for sharing filters from parent
const DashboardFilterContext = createContext();

const formatNumber = (num) => {
  return new Intl.NumberFormat("en-US").format(num || 0);
};

function FinanceDashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("token");
  const organizationId =
    localStorage.getItem("organizationId") === "null" ||
    localStorage.getItem("organizationId") === "0"
      ? 0
      : localStorage.getItem("organizationId");

  // Get filters from parent via context
  const filters = useContext(DashboardFilterContext) || {};
  const selectedCenter = filters.selectedCenter;
  const selectedClass = filters.selectedClass;
  const selectedSession = filters.selectedSession;

  useEffect(() => {
    const fetchData = async () => {
      const centerId =
        selectedCenter?.value || localStorage.getItem("centerId") || 0;
      const classId = selectedClass?.value || 0;
      const sessionId = selectedSession?.value || 0;

      try {
        setLoading(true);
        const response = await get(
          `Dashboard/GetAllDashboardStudentStaffRevenueAndCampusAsync?organizationID=${organizationId}&centerId=${centerId}&classId=${classId}&sessionId=${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setDashboardData(response);
      } catch (err) {
        console.error("Finance API Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedCenter, selectedClass, selectedSession, token, organizationId]);

  const stats = dashboardData?.summaryStatistics?.[0] || {};
  const revenueAndExpenseData = dashboardData?.revenueAndExpense || [];

  // Dynamic Line Chart
  const lineChartData = {
    labels: revenueAndExpenseData.map((item) => item.monthName || "N/A"),
    datasets: [
      {
        label: "Revenue",
        data: revenueAndExpenseData.map((item) => item.revenue || 0),
        borderColor: "#3498db",
        backgroundColor: "rgba(52,152,219,0.1)",
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "#3498db",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
      {
        label: "Expense",
        data: revenueAndExpenseData.map((item) => item.expense || 0),
        borderColor: "#e74c3c",
        backgroundColor: "rgba(231,76,60,0.1)",
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "#e74c3c",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const collectionEfficiency = stats.collectionEfficiency
    ? Math.round(stats.collectionEfficiency)
    : 0;

  if (loading) return <FullScreenLoader />;

  return (
    <div>
      {/* TILES — EXACTLY SAME AS YOUR ORIGINAL */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-4">
          <div
            className="card rounded-3 p-3 shadow-sm"
            style={{ borderLeft: "4px solid #3498db" }}
          >
            <div className="d-flex align-items-center">
              <div
                className="me-3"
                style={{ color: "#3498db", fontSize: "28px" }}
              >
                <i className="fas fa-hand-holding-usd"></i>
              </div>
              <div>
                <div className="text-uppercase fs-8 text-muted fw-bold">
                  Revenue (YTD)
                </div>
                <div className="h4 mb-0 fw-bold" style={{ color: "#2c3e50" }}>
                  Rs. {formatNumber(stats.totalRevenue)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-6 col-md-4">
          <div
            className="card rounded-3 p-3 shadow-sm"
            style={{ borderLeft: "4px solid #e67e22" }}
          >
            <div className="d-flex align-items-center">
              <div
                className="me-3"
                style={{ color: "#e67e22", fontSize: "28px" }}
              >
                <i className="fas fa-file-invoice-dollar"></i>
              </div>
              <div>
                <div className="text-uppercase fs-8 text-muted fw-bold">
                  Outstanding
                </div>
                <div className="h4 mb-0 fw-bold" style={{ color: "#2c3e50" }}>
                  Rs. {formatNumber(stats.feeReceived)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-6 col-md-4">
          <div
            className="card rounded-3 p-3 shadow-sm"
            style={{ borderLeft: "4px solid #2ecc71" }}
          >
            <div className="d-flex align-items-center">
              <div
                className="me-3"
                style={{ color: "#2ecc71", fontSize: "28px" }}
              >
                <i className="fas fa-chart-pie"></i>
              </div>
              <div>
                <div className="text-uppercase fs-8 text-muted fw-bold">
                  Collection Efficiency
                </div>
                <div className="h4 mb-0 fw-bold" style={{ color: "#2c3e50" }}>
                  {collectionEfficiency}%
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts — 100% ORIGINAL LAYOUT */}
      <div className="row g-4">
        <div className="col-lg-7">
          <div className="card mb-3 shadow-sm">
            <div className="card-body">
              <h6
                className="card-title fw-bold"
                style={{ color: "#2c3e50", marginBottom: "20px" }}
              >
                <i
                  className="fas fa-money-bill-wave me-2"
                  style={{ color: "#9b59b6" }}
                ></i>
                Finance: Revenue vs Expense (Monthly)
              </h6>
              <div style={{ position: "relative", height: "250px" }}>
                <Line
                  data={lineChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "top",
                        labels: {
                          padding: 15,
                          usePointStyle: true,
                          pointStyle: "circle",
                          font: { size: 11, weight: "600" },
                        },
                      },
                      tooltip: {
                        callbacks: {
                          label: (c) =>
                            `${c.dataset.label}: Rs. ${(
                              c.raw / 1000000
                            ).toFixed(1)}M`,
                        },
                      },
                    },
                    scales: {
                      x: {
                        grid: { color: "rgba(0,0,0,0.1)" },
                        ticks: { font: { size: 10 } },
                      },
                      y: {
                        grid: { color: "rgba(0,0,0,0.1)" },
                        ticks: {
                          callback: (v) =>
                            "Rs. " + (v / 1000000).toFixed(0) + "M",
                        },
                        title: { display: true, text: "Amount (Rs. Millions)" },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="card mb-3 shadow-sm">
            <div className="card-body">
              <h6
                className="card-title fw-bold"
                style={{ color: "#2c3e50", marginBottom: "20px" }}
              >
                <i
                  className="fas fa-clock me-2"
                  style={{ color: "#34495e" }}
                ></i>
                Accounts: Receivables Aging
              </h6>
              <div style={{ position: "relative", height: "250px" }}>
                <Bar
                  data={{
                    labels: ["0-30", "31-60", "61-90", "91-120", "120+"],
                    datasets: [
                      {
                        data: [0, 0, 0, 0, 0],
                        backgroundColor: [
                          "#e67e22",
                          "#d35400",
                          "#ba4a00",
                          "#a04000",
                          "#873600",
                        ],
                        borderRadius: 6,
                      },
                    ],
                  }}
                  options={{
                    indexAxis: "y",
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      x: {
                        ticks: {
                          callback: (v) =>
                            "Rs. " + (v / 1000000).toFixed(1) + "M",
                        },
                        title: { display: true, text: "Amount (Rs. Millions)" },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Wrapper to receive filters from parent
function FinanceDashboardWithFilters({ filters }) {
  return (
    <DashboardFilterContext.Provider value={filters}>
      <FinanceDashboard />
    </DashboardFilterContext.Provider>
  );
}

export default FinanceDashboardWithFilters;
